/**
 * useMediaQuery Hook
 *
 * Provides a React hook for responsive media query matching.
 * Automatically handles listener cleanup and re-renders on media query changes.
 *
 * Usage:
 *   const isDesktop = useMediaQuery(MEDIA_QUERIES.desktopAndUp);
 *   const isMobile = useMediaQuery(MEDIA_QUERIES.phone);
 */

import { useState, useEffect } from 'react';

/**
 * Hook to detect if a media query matches
 * @param {string} query - Media query string (e.g., "(min-width: 1024px)")
 * @returns {boolean} True if the media query matches the current viewport
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // Only run on client-side
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) return;

    const mediaQueryList = window.matchMedia(query);

    // Set initial state
    setMatches(mediaQueryList.matches);

    // Create listener function
    const listener = (e) => {
      setMatches(e.matches);
    };

    // Add listener (handle both old and new API)
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', listener);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(listener);
    }

    // Cleanup
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', listener);
      } else {
        mediaQueryList.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
}
